import {
  CategoryHeaderContainer,
  CategoryHeaderSubtitle,
  CategoryHeaderTitle,
  CTABlockContainer,
  CTAFrame,
  CTASubtitle,
  CTATitle,
  Partners,
} from '@cointracker/styleguide';
import { CategoryHeaderCopy } from '@cointracker/styleguide/src/CategoryHeader/CategoryHeader';
import { Grid } from '@cointracker/styleguide/src/Grid';
import actionableInsightsUrl from '@cointracker/styleguide/src/icons/features/feature-actionable-insights.webp?url';
import comprehensiveCoverageUrl from '@cointracker/styleguide/src/icons/features/feature-comprehensive-coverage.webp?url';
import painFreeTaxFilingUrl from '@cointracker/styleguide/src/icons/features/feature-pain-free-tax-filing.webp?url';
import securityUrl from '@cointracker/styleguide/src/icons/features/feature-security.webp?url';
import taxOptimizationUrl from '@cointracker/styleguide/src/icons/features/feature-tax-optimzation.webp?url';
import coinbaseIconUrl from '@cointracker/styleguide/src/icons/partners/Coinbase.svg?url';
import hrBlockIconUrl from '@cointracker/styleguide/src/icons/partners/H&R Block.svg?url';
import intuitIconUrl from '@cointracker/styleguide/src/icons/partners/Intuit TurboTax.svg?url';
import metamaskIconUrl from '@cointracker/styleguide/src/icons/partners/MetaMask.svg?url';
import openseaIconUrl from '@cointracker/styleguide/src/icons/partners/OpenSea.svg?url';
import phantomIconUrl from '@cointracker/styleguide/src/icons/partners/Phantom.svg?url';
import solanaIconUrl from '@cointracker/styleguide/src/icons/partners/Solana Foundation.svg?url';
import uniswapIconUrl from '@cointracker/styleguide/src/icons/partners/Uniswap.svg?url';
import {
  LANDING_SOCIAL_TESTIMONIALS,
  SocialTestimonialBlock,
} from '@cointracker/styleguide/src/TestimonialBlock';
import { REBRAND_URLS, useSearchParams } from '@cointracker/ui';
import { analytics } from '../../../common';
import {
  buildGetStartedUrl,
  getNextUrl,
} from '../../../pages-react/landing/utils';
import { FAQAccordion } from '../FAQAccordion';
import { Feature } from '../Feature';
import { CallToActionButton } from '../LandingCTAs';

export const FeaturesLanding = () => {
  const [searchParams] = useSearchParams();
  const nextUrl = getNextUrl(searchParams);
  const getStartedUrl = buildGetStartedUrl(nextUrl);
  const handleTrackingClick = (section: string) => {
    analytics.track('Feature Landing Page CTA', { section });
    window.location.href = getStartedUrl;
  };

  return (
    <div>
      <Grid>
        <CategoryHeaderContainer>
          <CategoryHeaderCopy>
            <CategoryHeaderTitle>
              Crypto tracking and tax reporting features
            </CategoryHeaderTitle>
            <CategoryHeaderSubtitle>
              Join the 2.5M+ people who trust CoinTracker with their crypto
              taxes, crypto portfolio tracking, and more
            </CategoryHeaderSubtitle>
            <div>
              <CallToActionButton
                href={REBRAND_URLS.GET_STARTED}
                buttonText="Get started"
                pageTitle="features"
                sectionName="FeaturesCTA"
                sectionTitle="Crypto tracking and tax reporting features"
              >
                Get started
              </CallToActionButton>
            </div>
          </CategoryHeaderCopy>
        </CategoryHeaderContainer>
      </Grid>

      <Feature
        title="Pain-free tax filing"
        subtitle="Automatically generate the tax forms you need and file with TurboTax, H&R Block, or your own tax professional. CoinTracker saves you time, money, and headaches."
        buttonText="Get started"
        imgSrc={painFreeTaxFilingUrl}
        imgAlt="Gradient spheres and structures evoke simplicity and ease" // TODO: AI generated
        handleTracking={() => handleTrackingClick('pain-free-tax-filing')}
      />

      <Feature
        title="Built-in tax optimization"
        subtitle="No one likes paying more tax than they have to. Get access to plans that include tax loss harvesting and tax lots breakdown."
        buttonText="Save on taxes"
        imgSrc={taxOptimizationUrl}
        imgAlt="Intertwined shapes represent streamlined solutions" // TODO: AI generated
        handleTracking={() => handleTrackingClick('tax-optimization')}
      />

      <Feature
        title="Comprehensive portfolio tracking"
        subtitle="Track all your crypto activity, all in one place. CoinTracker supports 500+ exchanges, wallets, and blockchains, 50k+ smart contracts, 600+ dapps, and CSV uploads."
        buttonText="Track your crypto"
        imgSrc={comprehensiveCoverageUrl}
        imgAlt="Layered design conveys depth and thoroughness" // TODO: AI generated
        handleTracking={() => handleTrackingClick('comprehensive-coverage')}
      />

      <Feature
        title="Actionable, data-driven insights"
        subtitle="Always know how the markets are affecting your holdings. Real-time calculations of unrealized and realized gains and losses, portfolio value, performance over time, and more."
        buttonText="See more"
        imgSrc={actionableInsightsUrl}
        imgAlt="Curved forms suggest clarity and actionable direction" // TODO: AI generated
        handleTracking={() => handleTrackingClick('actionable-insights')}
      />

      <Feature
        title="Industry-leading security"
        subtitle="Your data is in safe hands with read-only access to your wallets, end-to-end encryption, and token-based two-factor authentication."
        buttonText="Get CoinTracker"
        imgSrc={securityUrl}
        imgAlt="Bold arcs symbolize protection and reliability" // TODO: AI generated
        handleTracking={() => handleTrackingClick('security-at-every-step')}
      />

      <Partners
        title="Official tax partners"
        subtitle="We partner with the best to make tax time a breeze"
        topPartners={[
          { logoUrl: coinbaseIconUrl, text: 'Coinbase' },
          { logoUrl: intuitIconUrl, text: 'Intuit TurboTax' },
          { logoUrl: hrBlockIconUrl, text: 'H&R Block' },
          { logoUrl: uniswapIconUrl, text: 'Uniswap' },
        ]}
        bottomPartners={[
          { logoUrl: openseaIconUrl, text: 'Open Sea' },
          { logoUrl: metamaskIconUrl, text: 'MetaMask' },
          { logoUrl: phantomIconUrl, text: 'Phantom' },
          { logoUrl: solanaIconUrl, text: 'Solana Foundation' },
        ]}
      />

      <SocialTestimonialBlock
        title="Don't just take our word for it"
        subtext="What our customers are saying"
        testimonials={LANDING_SOCIAL_TESTIMONIALS}
        alignLeft
      />

      <FAQAccordion className="mt-120" />

      <CTABlockContainer framed>
        <CTAFrame>
          <CTATitle>
            Save time.
            <br />
            Save money.
          </CTATitle>
          <CTASubtitle>
            Join the 2.5M+ customers who trust CoinTracker with their crypto
            taxes
          </CTASubtitle>

          <CallToActionButton
            href={REBRAND_URLS.GET_STARTED}
            buttonText="Start for free"
            pageTitle="features"
            sectionName="SaveTimeMoneyCTA"
            sectionTitle="Save Time. Save Money."
          >
            Start for free
          </CallToActionButton>
        </CTAFrame>
      </CTABlockContainer>
    </div>
  );
};
